/* eslint max-classes-per-file: "off" */
import Specification from "./spec-pattern";

export const CARD_TYPES = {
  not_defined: "not_defined",
  amex: "amex",
  visa: "visa",
  mastercard: "mastercard",
  discover: "discover"
};

export const CARD_MASKS = {
  default: "9999999999999999999",
  amex: "9999 999999 99999",
  by4: "9999 9999 9999 9999"
};

const createStringRange = (start, end) =>
  Array(end - start + 1)
    .fill()
    .map((_, idx) => `${start + idx}`);

const CARD_RULES = {
  [CARD_TYPES.amex]: {
    starts: [["34", "37"]],
    length: [15]
  },
  [CARD_TYPES.visa]: {
    starts: [["4"]],
    length: [13, 14, 15, 16]
  },
  [CARD_TYPES.mastercard]: {
    starts: [createStringRange(51, 55), createStringRange(2221, 2720)],
    length: [16]
  },
  [CARD_TYPES.discover]: {
    starts: [
      ["6011"],
      createStringRange(622126, 622925),
      createStringRange(644, 649),
      ["65"]
    ],
    length: [16, 17, 18, 19]
  }
};

const DIGITS_PATTERN = /^[0-9]{13,19}$/;

const checkStarts = (value, cardType) =>
  CARD_RULES[cardType].starts.some(range =>
    value.length < range[0].length
      ? false
      : range.includes(value.substring(0, range[0].length))
  );

class DigitsOnly extends Specification {
  isSatisfiedBy = value => DIGITS_PATTERN.test(value);
}

class LunhAlgorithm extends Specification {
  isSatisfiedBy = value => {
    let nCheck = 0;
    let bEven = false;

    for (let n = value.length - 1; n >= 0; n -= 1) {
      const cDigit = value.charAt(n);
      let nDigit = parseInt(cDigit, 10);

      if (bEven) {
        nDigit *= 2;
      }
      if (bEven && nDigit > 9) {
        nDigit -= 9;
      }

      nCheck += nDigit;
      bEven = !bEven;
    }

    return nCheck % 10 === 0;
  };
}

class AmexStarts extends Specification {
  isSatisfiedBy = value => checkStarts(value, CARD_TYPES.amex);
}

class VisaStarts extends Specification {
  isSatisfiedBy = value => checkStarts(value, CARD_TYPES.visa);
}

class MastercardStarts extends Specification {
  isSatisfiedBy = value => checkStarts(value, CARD_TYPES.mastercard);
}

class DiscoverStarts extends Specification {
  isSatisfiedBy = value => checkStarts(value, CARD_TYPES.discover);
}

const genericRule = new DigitsOnly().and(new LunhAlgorithm());

const visaStartsRule = new VisaStarts();

const mastercardStartsRule = new MastercardStarts();

const amexStartsRule = new AmexStarts();

const discoverStartsRule = new DiscoverStarts();

export const defineCardType = value => {
  if (visaStartsRule.isSatisfiedBy(value)) {
    return CARD_TYPES.visa;
  }

  if (mastercardStartsRule.isSatisfiedBy(value)) {
    return CARD_TYPES.mastercard;
  }

  if (amexStartsRule.isSatisfiedBy(value)) {
    return CARD_TYPES.amex;
  }

  if (discoverStartsRule.isSatisfiedBy(value)) {
    return CARD_TYPES.discover;
  }

  return CARD_TYPES.not_defined;
};

export const validateCard = value =>
  // assume all other types of cards are allowed - from 13 till 19 digits
  genericRule.isSatisfiedBy(value);

export const defineCardMask = value => {
  const cardType = defineCardType(value);

  switch (cardType) {
    case CARD_TYPES.amex:
      return CARD_MASKS.amex;
    case CARD_TYPES.visa:
    case CARD_TYPES.mastercard:
    case CARD_TYPES.discover:
      return CARD_MASKS.by4;
    default:
      return CARD_MASKS.default;
  }
};
